/**
 * MatchViewModal — live / replay match visualiser  v7
 * - Pitch always goal-at-top: AttackingPitchMap + FullPitchMap rotated 180°
 * - ShotScene: top row players/outcome, bottom row pitch with ⚽ emoji ball in GoalFrame
 * - Lineup-based goalkeeper lookup (real photo + name)
 * - Blocker detection: finds adjacent block event and shows that player
 * - xG Race Chart: builds live as you step through the replay
 */

import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';
import { Button } from './ui/button';
import {
    X, Play, Pause, SkipBack, SkipForward,
    Radio, ChevronRight, Loader2,
} from 'lucide-react';

const API_URL     = process.env.REACT_APP_BACKEND_URL;

// Broadcast WC-match flag through the component tree without prop drilling
const WcImagesCtx = createContext(false);

// ── Zone mappings ──────────────────────────────────────────────────────────────

const PITCH_ZONE = {
    // ── Attacking half ──────────────────────────────────────────────────────
    'left penalty area':  { x: 22, y: 78 },
    'right penalty area': { x: 78, y: 78 },
    'centre of box':      { x: 50, y: 86 },
    'centre':             { x: 50, y: 50 },   // outside the box, mid-attacking half
    'left of box':        { x: 28, y: 57 },
    'right of box':       { x: 72, y: 57 },
    'outside box':        { x: 50, y: 50 },
    'left wing':          { x: 10, y: 48 },
    'right wing':         { x: 90, y: 48 },
    'penalty spot':       { x: 50, y: 88 },
    '6 yard box':         { x: 50, y: 94 },
    'left 6 yard':        { x: 32, y: 94 },
    'right 6 yard':       { x: 68, y: 94 },
    'left channel':       { x: 15, y: 72 },
    'right channel':      { x: 85, y: 72 },
    // ── Around halfway / own half — pos.y ≤ 0 puts the dot ON the halfway line
    //    after the y-flip (py = 8 + y*1.84 → viewport y = 200-py ≈ 196)
    'own half':           { x: 50, y: -2 },
    'halfway':            { x: 50, y: -2 },
    'centre circle':      { x: 50, y: -2 },
    'defensive third':    { x: 50, y: -5 },
    'left defensive':     { x: 18, y: -5 },
    'right defensive':    { x: 82, y: -5 },
    // ── Corner / byline positions (goal line, far left or far right) ────────
    'left corner':        { x: 3,  y: 98 },
    'right corner':       { x: 97, y: 98 },
    'corner':             { x: 3,  y: 98 },
    'left byline':        { x: 3,  y: 98 },
    'right byline':       { x: 97, y: 98 },
    'byline':             { x: 50, y: 98 },
    'left goal line':     { x: 3,  y: 98 },
    'right goal line':    { x: 97, y: 98 },
    // Goal kicks — always from the GK's 6-yard box; used with FullPitchMap so
    // the large negative y places the dot near the defending goal (bottom after flip)
    '__goal_kick__':      { x: 50, y: -85 },
};

const getPitchPos = (raw) => PITCH_ZONE[(raw || '').toLowerCase().trim()] || { x: 50, y: 70 };

/**
 * Map whereTo / outcome → x position for the pitch arrow tip.
 * whereTo (on-target): aims inside the goal mouth (98–142).
 * outcome (off-target): "Miss Left" / "Wide Left" → outside left post (~65);
 *                       "Miss Right" / "Wide Right" → outside right post (~175).
 */
const getGoalAimX = (whereTo, outcome) => {
    const wt = (whereTo  || '').toLowerCase();
    const oc = (outcome  || '').toLowerCase();

    // On-target: use whereTo lateral position inside the goal
    if (wt.includes('left'))  return 104;
    if (wt.includes('right')) return 136;

    // Off-target: parse outcome direction (miss/wide)
    if (oc.includes('left'))  return 65;   // wide of left post
    if (oc.includes('right')) return 175;  // wide of right post

    return 120; // centre / unknown
};

/**
 * Ball pixel position inside GoalFrame SVG (viewBox 0 0 380 260)
 * Net interior: x 49→331 (282px), y 34→220 (186px)
 */
const GOAL_BALL_POS = {
    'top left':      { x: 95,  y: 68  },
    'top centre':    { x: 190, y: 68  },
    'top right':     { x: 285, y: 68  },
    'high left':     { x: 95,  y: 68  },
    'high centre':   { x: 190, y: 68  },
    'high right':    { x: 285, y: 68  },
    'left high':     { x: 95,  y: 68  },
    'centre high':   { x: 190, y: 68  },
    'right high':    { x: 285, y: 68  },
    'left middle':   { x: 95,  y: 130 },
    'centre middle': { x: 190, y: 130 },
    'right middle':  { x: 285, y: 130 },
    'middle left':   { x: 95,  y: 130 },
    'middle centre': { x: 190, y: 130 },
    'middle right':  { x: 285, y: 130 },
    'left low':      { x: 95,  y: 185 },
    'centre low':    { x: 190, y: 185 },
    'right low':     { x: 285, y: 185 },
    'low left':      { x: 95,  y: 185 },
    'low centre':    { x: 190, y: 185 },
    'low right':     { x: 285, y: 185 },
    'bottom left':   { x: 95,  y: 185 },
    'bottom centre': { x: 190, y: 185 },
    'bottom right':  { x: 285, y: 185 },
};

const getBallPos = (raw) => GOAL_BALL_POS[(raw || '').toLowerCase().trim()] || null;

// ── Event type helpers ─────────────────────────────────────────────────────────

// Short code for timeline badge (no emoji)
const EVENT_SHORT = {
    'goal':            'G',
    'own goal':        'OG',
    'penalty goal':    'PG',
    'penalty miss':    'PM',
    'shot on target':  'SH',
    'shot off target': 'SH',
    'blocked shot':    'SH',
    'yellow card':     'YC',
    'red card':        'RC',
    'yellow/red card': 'RC',
    'substitution':    'SUB',
    'foul':            'FO',
    'corner':          'CK',
    'offside':         'OS',
    'free kick':       'FK',
    'penalty':         'PEN',
    'block':           'BLK',
    'save':            'SV',
    'tackle':          'TK',
    'throw in':        'TI',
    'goal kick':       'GK',
    'kick off':        'KO',
    'clearance':       'CLR',
    'assist':          'AST',
};

const EVENT_DOT = {
    'goal':            '#10b981',
    'own goal':        '#10b981',
    'penalty goal':    '#10b981',
    'penalty miss':    '#ef4444',
    'shot on target':  '#cbd5e1',
    'shot off target': '#64748b',
    'blocked shot':    '#f59e0b',
    'yellow card':     '#eab308',
    'red card':        '#ef4444',
    'yellow/red card': '#ef4444',
    'substitution':    '#6b7280',
    'foul':            '#f59e0b',
    'corner':          '#64748b',
    'offside':         '#64748b',
    'free kick':       '#60a5fa',
    'penalty':         '#a78bfa',
    'block':           '#f59e0b',
    'save':            '#60a5fa',
    'tackle':          '#f59e0b',
    'throw in':        '#475569',
    'goal kick':       '#475569',
    'kick off':        '#475569',
    'clearance':       '#64748b',
    'assist':          '#3b82f6',
};

const isShot     = t => ['goal','own goal','penalty goal','penalty miss',
                          'shot on target','shot off target','blocked shot'].includes(t);
const isCard     = t => ['yellow card','red card','yellow/red card'].includes(t);
const isFoul     = t => t === 'foul';
const isSub      = t => t === 'substitution';
const isGoal     = t => ['goal','own goal','penalty goal'].includes(t);
// Set pieces that get the pitch-map treatment
const isSetPiece   = t => ['free kick','corner','penalty','offside'].includes(t);
// Simple events: just big player photo + label, no pitch
const isSimpleHero = t => ['clearance','throw in','goal kick','kick off','block','header'].includes(t);
const isSaveEvt  = t => t === 'save' || t === 'goalkeeper save';

// ── Shared atoms ───────────────────────────────────────────────────────────────

function PlayerPhoto({ playerId, playerName, size = 'lg', ringColor = '#10b981', className = '' }) {
    const [err, setErr] = useState(false);
    const useWcImages = useContext(WcImagesCtx);
    const d = { sm: 'w-10 h-10', md: 'w-16 h-16', lg: 'w-20 h-20', xl: 'w-24 h-24', '2xl': 'w-40 h-40' }[size] || 'w-20 h-20';
    const txt = { sm: 'text-sm', md: 'text-xl', lg: 'text-2xl', xl: 'text-3xl', '2xl': 'text-5xl' }[size] || 'text-2xl';
    const ring = (size === 'xl' || size === '2xl') ? 3 : 2;
    const style = { border: `${ring}px solid ${ringColor}` };

    if (!err && playerId && playerId !== '0') {
        if (useWcImages) {
            const src = wcPlayerUrl(playerId);
            return (
                <div
                    className={`${d} rounded-full flex-shrink-0 bg-slate-800 ${className}`}
                    style={{
                        ...style,
                        backgroundImage: `url(${src})`,
                        backgroundSize: 'auto 230%',
                        backgroundPosition: '50% 5%',
                        backgroundRepeat: 'no-repeat',
                    }}
                    role="img" aria-label={playerName || ''}
                >
                    <img src={src} alt="" className="sr-only" onError={() => setErr(true)} />
                </div>
            );
        }
        return (
            <img src={playerUrl(playerId)} alt={playerName || ''}
                className={`${d} rounded-full object-cover object-top flex-shrink-0 bg-slate-800 ${className}`}
                style={style} onError={() => setErr(true)} />
        );
    }
    return (
        <div className={`${d} rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 ${className}`}
            style={style}>
            <span className={`${txt} font-bold text-slate-400`}>
                {(playerName || '?')[0].toUpperCase()}
            </span>
        </div>
    );
}

/** Keeper circle: shows photo if available, else team badge, else styled fallback */
function KeeperDisplay({ playerId, playerName, teamId, size = 'xl' }) {
    const [photoErr,  setPhotoErr]  = useState(false);
    const [badgeErr,  setBadgeErr]  = useState(false);
    const useWcImages = useContext(WcImagesCtx);
    const d = { md: 'w-16 h-16', lg: 'w-20 h-20', xl: 'w-24 h-24' }[size] || 'w-24 h-24';
    const hasPhoto = !photoErr && playerId && playerId !== '0';

    return (
        <div className="flex flex-col items-center gap-1.5">
            <div
                className={`${d} rounded-full bg-slate-800 flex items-center justify-center overflow-hidden flex-shrink-0`}
                style={hasPhoto && useWcImages ? {
                    border: '3px solid #60a5fa',
                    backgroundImage: `url(${wcPlayerUrl(playerId)})`,
                    backgroundSize: 'auto 230%',
                    backgroundPosition: '50% 5%',
                    backgroundRepeat: 'no-repeat',
                } : { border: '3px solid #60a5fa' }}
            >
                {hasPhoto && useWcImages ? (
                    <img src={wcPlayerUrl(playerId)} alt="" className="sr-only" onError={() => setPhotoErr(true)}/>
                ) : hasPhoto ? (
                    <img src={playerUrl(playerId)}
                        alt={playerName || 'Goalkeeper'}
                        className="w-full h-full object-cover object-top" onError={() => setPhotoErr(true)}/>
                ) : !badgeErr && teamId ? (
                    <img src={badgeUrl(teamId)} alt=""
                        className="w-10 h-10 object-contain p-1"
                        onError={() => setBadgeErr(true)}/>
                ) : (
                    <span className="text-slate-400 text-xs font-bold tracking-widest">GK</span>
                )}
            </div>
            <div className="text-center">
                <p className="font-bold text-xs text-white leading-tight truncate max-w-[88px]">
                    {playerName || 'Goalkeeper'}
                </p>
                <p className="text-[10px] text-slate-400">GK</p>
            </div>
        </div>
    );
}

function TeamBadgeSm({ teamId, teamName }) {
    const [err, setErr] = useState(false);
    if (!err && teamId)
        return <img src={badgeUrl(teamId)} alt={teamName || ''}
            className="w-5 h-5 object-contain" onError={() => setErr(true)}/>;
    return <span className="text-[10px] text-slate-400">{teamName?.charAt(0)}</span>;
}

// ── Goal Frame SVG ─────────────────────────────────────────────────────────────

function GoalFrame({ whereTo, outcome, isGoalEvent }) {
    const ballPos = getBallPos(whereTo);
    const outcomeLC = (outcome || '').toLowerCase();
    const isSaved   = outcomeLC.includes('save');
    const isBlocked = outcomeLC.includes('block');
    const isScored  = isGoalEvent || outcomeLC === 'goal';

    const ballColor = isScored ? '#10b981' : isSaved ? '#3b82f6' : '#f59e0b';

    return (
        <svg viewBox="0 0 380 260" className="w-full">
            <defs>
                <pattern id="mvNet" x="49" y="34" width="14" height="14" patternUnits="userSpaceOnUse">
                    <line x1="0" y1="0" x2="14" y2="14" stroke="rgba(255,255,255,0.2)" strokeWidth="0.65"/>
                    <line x1="14" y1="0" x2="0" y2="14" stroke="rgba(255,255,255,0.2)" strokeWidth="0.65"/>
                </pattern>
                {ballPos && (
                    <radialGradient id="mvBallGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor={ballColor} stopOpacity="0.8"/>
                        <stop offset="100%" stopColor={ballColor} stopOpacity="0"/>
                    </radialGradient>
                )}
                {isScored && (
                    <radialGradient id="mvGoalFlash" cx="50%" cy="40%" r="60%">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.3"/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
                    </radialGradient>
                )}
                <linearGradient id="mvPostV" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"   stopColor="#7f8ea3"/>
                    <stop offset="35%"  stopColor="#ffffff"/>
                    <stop offset="100%" stopColor="#5a6a80"/>
                </linearGradient>
                <linearGradient id="mvPostH" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#7f8ea3"/>
                    <stop offset="35%"  stopColor="#ffffff"/>
                    <stop offset="100%" stopColor="#5a6a80"/>
                </linearGradient>
                <clipPath id="mvNetClip">
                    <rect x="49" y="34" width="282" height="186"/>
                </clipPath>
            </defs>

            {/* Background */}
            <rect width="380" height="260" fill="#080f1c"/>
            {/* Ambient pitch line at base */}
            <rect x="0" y="218" width="380" height="42" fill="#0e2010"/>
            <rect x="0" y="218" width="380" height="2" fill="#1a4a1a"/>
            {/* Stadium haze top */}
            <ellipse cx="190" cy="-20" rx="300" ry="70" fill="rgba(255,255,200,0.025)"/>

            {/* Net */}
            <rect x="49" y="34" width="282" height="186" fill="rgba(140,170,210,0.04)"/>
            <rect x="49" y="34" width="282" height="186" fill="url(#mvNet)"/>

            {/* GOAL flash */}
            {isScored && (
                <rect x="49" y="34" width="282" height="186" fill="url(#mvGoalFlash)"/>
            )}

            {/* Left post */}
            <rect x="34" y="22" width="16" height="200" fill="url(#mvPostV)" rx="2"/>
            {/* Right post */}
            <rect x="330" y="22" width="16" height="200" fill="url(#mvPostV)" rx="2"/>
            {/* Crossbar */}
            <rect x="34" y="20" width="312" height="16" fill="url(#mvPostH)" rx="2"/>

            {/* 6-yard box ground indicator */}
            <rect x="130" y="215" width="120" height="4"
                fill="rgba(255,255,255,0.07)" rx="1"/>

            {/* Ball glow */}
            {ballPos && (
                <ellipse cx={ballPos.x} cy={ballPos.y} rx="44" ry="44"
                    fill="url(#mvBallGlow)" clipPath="url(#mvNetClip)"/>
            )}

            {/* Ball — ⚽ emoji */}
            {ballPos && (
                <g clipPath="url(#mvNetClip)">
                    {/* Outcome colour halo behind ball */}
                    <circle cx={ballPos.x} cy={ballPos.y} r="22" fill={ballColor} opacity="0.18"/>
                    <text x={ballPos.x} y={ballPos.y + 12} fontSize="34" textAnchor="middle"
                        style={{ userSelect: 'none', dominantBaseline: 'auto' }}>⚽</text>
                </g>
            )}

            {/* Keeper silhouette removed — real photo overlaid by parent component */}
        </svg>
    );
}

// ── Attacking-half pitch map (shots) — goal always at TOP ─────────────────────

function AttackingPitchMap({ whereFrom, whereTo, outcome, teamColour = '#10b981', noArrow = false, statsX, statsY }) {
    // Use precise theStatsAPI coordinates when available (x=dist from goal 0-100, y=left-right 0-100)
    // statsX≈11 = penalty spot, statsX≈0 = goal line, statsY≈50 = centre
    const pos = (statsX != null && statsY != null)
        ? { x: statsY, y: Math.max(5, Math.min(97, 98 - (statsX / 42) * 60)) }
        : getPitchPos(whereFrom);
    // Scale pos (0-100) into SVG coords (viewBox 0 0 240 200)
    // Raw layout: goal at BOTTOM (y≈192), halfway at TOP (y=4)
    // Entire content rotated 180° around centre so goal ends up at TOP
    const px       = 8 + pos.x * 2.24;
    const py       = 8 + pos.y * 1.84;
    // Arrow aims at correct goal side; off-target shots use outcome direction
    const goalAimX  = getGoalAimX(whereTo, outcome);
    // Suppress arrow for own-half positions, or when caller explicitly opts out (set pieces)
    const showArrow = !noArrow && pos.y > 0;
    // Arrow tip y: for positions well outside the box (y < 62) stop at the
    // penalty spot area (~y=148 original → viewport y=52) rather than goal line
    const arrowTipY = pos.y < 62 ? 148 : 192;

    return (
        <svg viewBox="0 0 240 200" className="w-full h-full" style={{ borderRadius: 6 }}>
            <defs>
                {/* Alternating grass stripes like lineup pitch */}
                <pattern id="mvGrass" x="0" y="0" width="30" height="200" patternUnits="userSpaceOnUse">
                    <rect x="0"  width="30" height="200" fill="#2d7d46"/>
                    <rect x="0"  width="15" height="200" fill="#2a7342"/>
                </pattern>
            </defs>

            {/* Flip y-axis only (scale -1) so goal moves to TOP, left stays left */}
            <g transform="scale(1,-1) translate(0,-200)">
                {/* Grass */}
                <rect width="240" height="200" fill="url(#mvGrass)" rx="5"/>
                {/* Pitch border */}
                <rect x="4" y="4" width="232" height="192" fill="none"
                    stroke="rgba(255,255,255,0.75)" strokeWidth="1.5"/>
                {/* Halfway line — now at BOTTOM after flip */}
                <line x1="4" y1="4" x2="236" y2="4"
                    stroke="rgba(255,255,255,0.9)" strokeWidth="2"/>

                {/* Penalty area */}
                <rect x="52" y="120" width="136" height="76" fill="none"
                    stroke="rgba(255,255,255,0.8)" strokeWidth="1.5"/>
                {/* 6-yard box */}
                <rect x="86" y="162" width="68" height="34" fill="none"
                    stroke="rgba(255,255,255,0.65)" strokeWidth="1.2"/>
                {/* Goal — ends up at TOP after flip */}
                <rect x="98" y="192" width="44" height="8"
                    fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5"/>
                {/* Penalty spot */}
                <circle cx="120" cy="148" r="2" fill="rgba(255,255,255,0.8)"/>
                {/* Penalty arc */}
                <path d="M 80 120 A 40 40 0 0 1 160 120" fill="none"
                    stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>

                {/* Player glow */}
                <circle cx={px} cy={py} r="18" fill={teamColour} opacity="0.22"/>
                {/* Player circle */}
                <circle cx={px} cy={py} r="12"
                    fill={teamColour} stroke="white" strokeWidth="2.5" opacity="0.95"/>
                {/* Inner white dot */}
                <circle cx={px} cy={py} r="5" fill="white" opacity="0.9"/>

                {/* Dashed trajectory — hidden for own-half; stops at penalty spot for distant positions */}
                {showArrow && (<>
                    <line x1={px} y1={py} x2={goalAimX} y2={arrowTipY}
                        stroke={teamColour} strokeWidth="2.5" strokeDasharray="7 4" opacity="0.9"/>
                    {/* X marker at shot destination */}
                    <line x1={goalAimX - 6} y1={arrowTipY - 6} x2={goalAimX + 6} y2={arrowTipY + 6}
                        stroke={teamColour} strokeWidth="2.5" strokeLinecap="round" opacity="0.95"/>
                    <line x1={goalAimX + 6} y1={arrowTipY - 6} x2={goalAimX - 6} y2={arrowTipY + 6}
                        stroke={teamColour} strokeWidth="2.5" strokeLinecap="round" opacity="0.95"/>
                </>)}
            </g>
        </svg>
    );
}

// ── Full-pitch map (fouls, cards, set pieces) — attacking goal always at TOP ───

function FullPitchMap({ whereFrom, teamColour = '#10b981', label = 'FOUL' }) {
    const pos = getPitchPos(whereFrom);
    // Raw layout: attacking half = BOTTOM half (sy 104-192), goal at very bottom
    // Entire content rotated 180° so attacking goal ends up at TOP
    const sx = 8 + pos.x * 2.84;
    const sy = 104 + pos.y * 0.88;

    return (
        <svg viewBox="0 0 300 210" className="w-full h-full">
            {/* Flip y-axis only so attacking goal moves to TOP, left stays left */}
            <g transform="scale(1,-1) translate(0,-210)">
                <rect width="300" height="210" fill="#1a4731" rx="3"/>
                {[0,1,2,3,4,5,6].map(i => (
                    <rect key={i} x={i*43} width="22" height="210" fill="rgba(0,0,0,0.06)"/>
                ))}
                {/* Pitch border */}
                <rect x="6" y="6" width="288" height="198" fill="none"
                    stroke="rgba(255,255,255,0.65)" strokeWidth="1.5"/>
                {/* Halfway */}
                <line x1="6" y1="105" x2="294" y2="105"
                    stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
                {/* Centre circle */}
                <circle cx="150" cy="105" r="28" fill="none"
                    stroke="rgba(255,255,255,0.45)" strokeWidth="1.2"/>
                <circle cx="150" cy="105" r="2" fill="rgba(255,255,255,0.7)"/>
                {/* Bottom penalty area (becomes TOP after flip — attacking) */}
                <rect x="78" y="158" width="144" height="46" fill="none"
                    stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
                <rect x="113" y="185" width="74" height="19" fill="none"
                    stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
                <circle cx="150" cy="173" r="1.5" fill="rgba(255,255,255,0.6)"/>
                {/* Top penalty area (becomes BOTTOM after flip — defending) */}
                <rect x="78" y="6" width="144" height="46" fill="none"
                    stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
                <rect x="113" y="6" width="74" height="19" fill="none"
                    stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
                <circle cx="150" cy="37" r="1.5" fill="rgba(255,255,255,0.6)"/>
                {/* Goals */}
                <rect x="124" y="200" width="52" height="7"
                    fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.6)" strokeWidth="1"/>
                <rect x="124" y="3" width="52" height="7"
                    fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.6)" strokeWidth="1"/>
                {/* Corner arcs */}
                <path d="M 6 18 A 12 12 0 0 1 18 6"    fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
                <path d="M 282 6 A 12 12 0 0 1 294 18"  fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
                <path d="M 294 192 A 12 12 0 0 1 282 204" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
                <path d="M 18 204 A 12 12 0 0 1 6 192"  fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
                {/* Event location dot */}
                <circle cx={sx} cy={sy} r="10"
                    fill={teamColour} stroke="white" strokeWidth="2.5" opacity="0.95"/>
                <circle cx={sx} cy={sy} r="4" fill="white" opacity="0.85"/>
            </g>
            {/* Label rendered outside the flipped group so text reads correctly.
                Viewport position: x stays same, y mirrors to (210 - (sy+21)) */}
            {label && (
                <text x={sx} y={210 - (sy + 21)} textAnchor="middle" fontSize="7.5"
                    fill={teamColour} fontWeight="bold" letterSpacing="1.5">
                    {label}
                </text>
            )}
        </svg>
    );
}

// ── Shot / Goal scene ──────────────────────────────────────────────────────────

function ShotScene({ event, homeTeamId, awayTeamId, homeKeeper, awayKeeper, allEvents, eventIdx, statsShot }) {
    const isHome    = event.team_id === homeTeamId;
    const colour    = isHome ? '#10b981' : '#3b82f6';
    const goal      = isGoal(event.type);
    // Enrichment from theStatsAPI
    const bodyPart  = statsShot?.body_part || null;
    const xg        = statsShot?.expected_goals ?? null;
    const outcomeLC = (event.outcome || '').toLowerCase();
    const isSaved   = outcomeLC.includes('save');
    const isBlkd    = outcomeLC.includes('block');
    const isPenMiss = event.type === 'penalty miss';
    const isOffTgt  = event.type === 'shot off target';
    // Find the adjacent block event — PA sends it immediately after the shot
    const blocker = useMemo(() => {
        if (!isBlkd || !allEvents) return null;
        for (let i = Math.max(0, (eventIdx ?? 0) - 2);
             i < Math.min(allEvents.length, (eventIdx ?? 0) + 4); i++) {
            const e = allEvents[i];
            if (e.type === 'block' &&
                Math.abs(e.minute - event.minute) <= 1 &&
                e.team_id !== event.team_id &&
                e.player_id && e.player_id !== '0') {
                return e;
            }
        }
        return null;
    }, [allEvents, eventIdx, event, isBlkd]);

    // Goalkeeper from lineup fetch
    const keeperData  = isHome ? awayKeeper : homeKeeper;
    const defendingId = isHome ? awayTeamId : homeTeamId;

    // whereTo is often blank on the shot — look ahead to the adjacent goal/save event
    const shotWhereTo = useMemo(() => {
        if (event.where_to) return event.where_to;
        if (!allEvents || eventIdx === null) return null;
        for (let i = eventIdx + 1; i < Math.min(allEvents.length, eventIdx + 3); i++) {
            if (allEvents[i].where_to) return allEvents[i].where_to;
        }
        return null;
    }, [event, allEvents, eventIdx]);

    // Right side: blocker wins over keeper
    const rightId   = blocker ? blocker.player_id   : keeperData?.player_id;
    const rightName = blocker ? blocker.player       : (keeperData?.player || 'Goalkeeper');
    const rightSub  = blocker ? blocker.display_time : 'GK';
    const rightRing = blocker ? '#f59e0b'            : '#60a5fa';

    // Shot events never show GOAL — that's a separate event type
    const outcomeLabel = goal        ? 'GOAL'
        : isSaved                    ? 'SAVED'
        : isBlkd                     ? 'BLOCKED'
        : isPenMiss                  ? 'PENALTY MISS'
        : isOffTgt                   ? 'OFF TARGET'
        : event.type === 'shot on target' ? 'ON TARGET'
        : (event.type).toUpperCase();

    const outcomeBg = goal
        ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-300'
        : isSaved
        ? 'bg-blue-500/20 border-blue-400/60 text-blue-300'
        : 'bg-amber-500/20 border-amber-400/50 text-amber-300';

    // ── Shared top row ─────────────────────────────────────────────────────────
    const topRow = (
        <div className="flex items-center justify-between gap-2 flex-shrink-0">

            {/* Attacker: photo + name/details stacked inline */}
            <div className="flex items-center gap-2" style={{ flex: '0 0 38%' }}>
                <PlayerPhoto
                    playerId={event.player_id}
                    playerName={event.player}
                    size="lg"
                    ringColor={colour}
                />
                <div className="min-w-0">
                    <p className="font-bold text-sm text-white leading-tight truncate">
                        {event.player || '—'}
                    </p>
                    {(bodyPart || event.how) && (
                        <p className="text-[11px] text-slate-300 font-medium">{bodyPart || event.how}</p>
                    )}
                    <p className="text-[10px] text-slate-500">{event.display_time}</p>
                    {event.where_from && !statsShot && (
                        <p className="text-[10px] text-slate-500 leading-tight">
                            From: <span className="text-slate-300">{event.where_from}</span>
                        </p>
                    )}
                    {statsShot?.situation && (
                        <p className="text-[10px] text-slate-500 leading-tight">{statsShot.situation}</p>
                    )}
                    {event.assist_player && (
                        <p className="text-[10px] text-blue-400 leading-tight mt-0.5">
                            🅰 {event.assist_player}
                        </p>
                    )}
                </div>
            </div>

            {/* Outcome badge + xG — centre */}
            <div className="flex-shrink-0 flex flex-col items-center gap-1">
                <div className={`px-3 py-1.5 rounded-xl border font-black
                    text-sm tracking-widest text-center ${outcomeBg}`}>
                    {outcomeLabel}
                </div>
                {xg !== null && (
                    <p className="text-[11px] font-bold text-amber-300/90 tabular-nums">
                        xG {xg.toFixed(2)}
                    </p>
                )}
            </div>

            {/* Keeper/Blocker: photo + name, right-aligned */}
            <div className="flex items-center gap-2 flex-row-reverse" style={{ flex: '0 0 38%' }}>
                {rightId && rightId !== '0' ? (
                    <PlayerPhoto
                        playerId={rightId}
                        playerName={rightName}
                        size="lg"
                        ringColor={rightRing}
                    />
                ) : (
                    <KeeperDisplay
                        playerId={rightId}
                        playerName={rightName}
                        teamId={defendingId}
                        size="lg"
                    />
                )}
                <div className="min-w-0 text-right">
                    <p className="font-bold text-sm text-white leading-tight truncate">
                        {rightName}
                    </p>
                    <p className="text-[10px] text-slate-400">{rightSub}</p>
                    {blocker && (
                        <p className="text-[10px] text-amber-400 font-semibold">BLOCK</p>
                    )}
                </div>
            </div>
        </div>
    );

    // ── GOAL: big centred goal frame + GK photo overlaid, no pitch ────────────
    if (goal) {
        return (
            <div className="flex flex-col w-full h-full px-3 py-2 gap-2">
                {topRow}
                {/* Big goal frame fills remaining height, GK photo overlaid */}
                <div className="flex-1 min-h-0 flex items-center justify-center">
                    <div className="relative w-full max-w-lg">
                        <GoalFrame
                            whereTo={event.where_to}
                            outcome="Goal"
                            isGoalEvent={true}
                        />
                        {/* GK photo overlaid at goal mouth centre */}
                        <div className="absolute pointer-events-none"
                            style={{ left: '50%', top: '74%', transform: 'translate(-50%,-50%)' }}>
                            <KeeperDisplay
                                playerId={keeperData?.player_id}
                                playerName={keeperData?.player || 'Goalkeeper'}
                                teamId={defendingId}
                                size="md"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── SHOT: full-width pitch (goal frame removed — save event handles that) ──
    return (
        <div className="flex flex-col w-full h-full px-3 py-2 gap-2">
            {topRow}
            {/* Full-width attacking pitch — uses precise statsapi coords when available */}
            <div className="flex-1 rounded-xl overflow-hidden min-h-0">
                <AttackingPitchMap
                    whereFrom={event.where_from}
                    whereTo={shotWhereTo}
                    outcome={event.outcome}
                    teamColour={colour}
                    statsX={statsShot?.x}
                    statsY={statsShot?.y}
                />
            </div>
        </div>
    );
}

// ── Card scene ─────────────────────────────────────────────────────────────────

function CardScene({ event, homeTeamId }) {
    const isRed = event.type === 'red card' || event.type === 'yellow/red card';
    const isYR  = event.type === 'yellow/red card';
    const colour = isRed ? '#ef4444' : '#eab308';

    return (
        <div className="flex flex-col items-center gap-5 py-4 px-4 w-full">
            {/* Player photo + card graphic */}
            <div className="flex items-center gap-6">
                <PlayerPhoto
                    playerId={event.player_id}
                    playerName={event.player}
                    size="xl"
                    ringColor={colour}
                />
                <div className="flex items-end gap-2">
                    {isYR && (
                        <div className="w-11 h-16 rounded-lg shadow-xl"
                            style={{ background: '#eab308', transform: 'rotate(-12deg)',
                                boxShadow: '0 6px 20px rgba(234,179,8,0.5)' }}/>
                    )}
                    <div className="rounded-lg shadow-2xl"
                        style={{
                            width: isYR ? 50 : 60, height: isYR ? 68 : 82,
                            background: isRed ? '#dc2626' : '#ca8a04',
                            transform: 'rotate(5deg)',
                            boxShadow: isRed
                                ? '0 8px 28px rgba(220,38,38,0.6)'
                                : '0 8px 28px rgba(202,138,4,0.6)',
                        }}/>
                </div>
            </div>

            <div className="text-center space-y-1.5">
                <p className="font-black text-xl text-white">{event.player}</p>
                <p className="text-sm text-slate-400 capitalize">
                    {event.type} · {event.display_time}
                </p>
            </div>

            {/* Pitch position if available */}
            {event.where_from && (
                <div className="w-full max-w-xs rounded-xl bg-white/5 border border-white/8 p-3">
                    <p className="text-[10px] text-slate-500 text-center mb-2 uppercase tracking-wider">
                        Location
                    </p>
                    <div className="h-28">
                        <FullPitchMap whereFrom={event.where_from}
                            teamColour={colour} label={isRed ? 'RED' : 'YELLOW'}/>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Foul scene ─────────────────────────────────────────────────────────────────

function FoulScene({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const colour = isHome ? '#10b981' : '#3b82f6';

    return (
        <div className="flex flex-col w-full h-full items-center justify-center gap-6 px-4 py-6">

            {/* Face-off row: fouler — FOULED — fouled */}
            <div className="flex items-center justify-center gap-4 w-full">

                {/* Fouler */}
                <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <PlayerPhoto
                        playerId={event.player_id}
                        playerName={event.player}
                        size="xl"
                        ringColor="#ef4444"
                    />
                    <p className="font-bold text-sm text-white text-center truncate w-full px-1">
                        {event.player || '—'}
                    </p>
                    <p className="text-[10px] text-slate-500">{event.display_time}</p>
                </div>

                {/* Centre word */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="w-px h-6 bg-slate-700"/>
                    <span className="text-xs font-black text-red-400 tracking-widest">FOULED</span>
                    <div className="w-px h-6 bg-slate-700"/>
                </div>

                {/* Fouled player */}
                <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <PlayerPhoto
                        playerId={event.player2_id}
                        playerName={event.player2}
                        size="xl"
                        ringColor="#3b82f6"
                    />
                    <p className="font-bold text-sm text-white text-center truncate w-full px-1">
                        {event.player2 || '—'}
                    </p>
                    {event.where_from && (
                        <p className="text-[10px] text-slate-500">{event.where_from}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Substitution scene ─────────────────────────────────────────────────────────

function SubScene({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const colour = isHome ? '#10b981' : '#3b82f6';

    return (
        <div className="flex flex-col items-center gap-5 py-5 px-4 w-full">
            <div className="flex items-center gap-5 w-full">
                {/* PA API: player_id (player1) = coming ON, player2_id (player2) = going OFF */}

                {/* Off — player2 */}
                <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <div className="relative">
                        <PlayerPhoto
                            playerId={event.player2_id}
                            playerName={event.player2}
                            size="xl"
                            ringColor="#ef4444"
                        />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full
                            bg-red-600 flex items-center justify-center shadow-md">
                            <svg width="10" height="10" viewBox="0 0 10 10">
                                <path d="M5 1 L5 9 M2 6 L5 9 L8 6"
                                    stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                        </div>
                    </div>
                    <p className="text-xs font-bold text-red-400 text-center truncate w-full px-1">
                        {event.player2 || '—'}
                    </p>
                    <span className="text-[10px] text-slate-500 font-semibold tracking-widest">OFF</span>
                </div>

                {/* Divider */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M4 12 H20 M14 6 L20 12 L14 18" stroke="#4b5563"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p className="text-[10px] text-slate-600">{event.display_time}</p>
                </div>

                {/* On — player1 */}
                <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                    <div className="relative">
                        <PlayerPhoto
                            playerId={event.player_id}
                            playerName={event.player}
                            size="xl"
                            ringColor="#10b981"
                        />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full
                            bg-emerald-600 flex items-center justify-center shadow-md">
                            <svg width="10" height="10" viewBox="0 0 10 10">
                                <path d="M5 9 L5 1 M2 4 L5 1 L8 4"
                                    stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                        </div>
                    </div>
                    <p className="text-xs font-bold text-emerald-400 text-center truncate w-full px-1">
                        {event.player || '—'}
                    </p>
                    <span className="text-[10px] text-slate-500 font-semibold tracking-widest">ON</span>
                </div>
            </div>
        </div>
    );
}

// ── Generic event ──────────────────────────────────────────────────────────────

// ── Simple hero scene — no pitch, just big player photo + label ───────────────
// Used for clearances, throw-ins, goal kicks, blocks, headers, etc.

const SIMPLE_LABEL = {
    'clearance':   'Clearance',
    'throw in':    'Throw In',
    'goal kick':   'Goal Kick',
    'block':       'Block',
    'header':      'Header',
    'kick off':    'Kick Off',
};

function SimpleHeroScene({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const colour = isHome ? '#10b981' : '#3b82f6';
    const label  = SIMPLE_LABEL[event.type] || event.type.replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div className="flex flex-col items-center justify-center gap-5 w-full h-full px-6 text-center">
            <PlayerPhoto
                playerId={event.player_id}
                playerName={event.player}
                size="2xl"
                ringColor={colour}
            />
            <div className="space-y-1">
                <p className="font-black text-2xl text-white tracking-wide">{label}</p>
                {event.player && (
                    <p className="text-slate-300 text-base font-semibold">{event.player}</p>
                )}
                <p className="text-slate-500 text-sm">{event.display_time}</p>
            </div>
        </div>
    );
}

function GenericScene({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const colour = isHome ? '#10b981' : '#3b82f6';

    return (
        <div className="flex flex-col items-center justify-center gap-5 w-full h-full px-6 text-center">
            <PlayerPhoto
                playerId={event.player_id}
                playerName={event.player}
                size="2xl"
                ringColor={colour}
            />
            <div className="space-y-1">
                <p className="font-black text-2xl capitalize text-white tracking-wide">
                    {event.type}
                </p>
                {event.player && (
                    <p className="text-slate-300 text-base font-semibold">{event.player}</p>
                )}
                <p className="text-slate-500 text-sm">{event.display_time}</p>
            </div>
        </div>
    );
}

// ── Set-piece scene (free kick, corner, penalty, offside) ─────────────────────

const SET_PIECE_LABEL = {
    'free kick': 'FREE KICK',
    'corner':    'CORNER',
    'penalty':   'PENALTY',
    'offside':   'OFFSIDE',
    'goal kick': 'GOAL KICK',
    'throw in':  'THROW IN',
    'kick off':  'KICK OFF',
};

function SetPieceScene({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const colour = isHome ? '#10b981' : '#3b82f6';
    const label  = SET_PIECE_LABEL[event.type] || event.type.toUpperCase();

    const dotCol = EVENT_DOT[event.type] || '#60a5fa';

    return (
        <div className="flex flex-col w-full h-full px-3 py-3 gap-3 overflow-y-auto">

            {/* Player info row */}
            <div className="flex items-center gap-3 flex-shrink-0">
                <PlayerPhoto
                    playerId={event.player_id}
                    playerName={event.player}
                    size="lg"
                    ringColor={colour}
                />
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-base text-white leading-tight truncate">
                        {event.player || '—'}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{event.display_time}</p>
                    {event.where_from && (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            From: <span className="text-slate-300">{event.where_from}</span>
                        </p>
                    )}
                    {event.outcome && event.outcome.toLowerCase() !== 'open play' && (
                        <p className="text-[11px] text-slate-500">
                            Outcome: <span className="text-slate-300">{event.outcome}</span>
                        </p>
                    )}
                </div>
                <div className={`flex-shrink-0 px-3 py-1.5 rounded-xl border font-black
                    text-xs tracking-widest`}
                    style={{ background: `${dotCol}18`, borderColor: `${dotCol}55`, color: dotCol }}>
                    {label}
                </div>
            </div>

            {/* Pitch — no arrow for set pieces (free kick, corner, penalty, offside) */}
            <div className="flex-1 min-h-0 rounded-xl overflow-hidden" style={{ minHeight: 200 }}>
                <AttackingPitchMap
                    whereFrom={event.where_from}
                    teamColour={colour}
                    noArrow
                />
            </div>
        </div>
    );
}

// ── Save scene ─────────────────────────────────────────────────────────────────

function SaveScene({ event }) {
    return (
        <div className="flex flex-col w-full h-full px-3 py-3 gap-3">

            {/* Hero keeper photo + name — centred, large */}
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <PlayerPhoto
                    playerId={event.player_id}
                    playerName={event.player || 'Goalkeeper'}
                    size="2xl"
                    ringColor="#60a5fa"
                />
                <div className="text-center">
                    <p className="font-black text-xl text-white leading-tight">
                        {event.player || 'Goalkeeper'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">Goalkeeper · {event.display_time}</p>
                </div>
                <div className="px-4 py-1.5 rounded-xl border font-black text-sm tracking-widest
                    bg-blue-500/20 border-blue-400/60 text-blue-300">
                    SAVED
                </div>
            </div>

            {/* Goal frame showing where the shot was aimed */}
            <div className="flex-1 min-h-0 flex items-center justify-center">
                <div className="w-full max-w-sm">
                    <GoalFrame
                        whereTo={event.where_to}
                        outcome="save"
                        isGoalEvent={false}
                    />
                </div>
            </div>
        </div>
    );
}

// ── Lineup idle screen — portrait pitch ───────────────────────────────────────

/** Same formation-grouping logic as MatchDetailPage; works with lineups API player shape */
function groupByFormationMV(formationStr, players) {
    const starters = [...(players || [])]
        .filter(p => p.status === 'Start')
        .sort((a, b) => (a.formation_place || 99) - (b.formation_place || 99));
    if (!starters.length) return [];
    if (formationStr) {
        const parts = formationStr.split('-').map(Number).filter(n => n > 0);
        const sizes = [1, ...parts];
        if (sizes.reduce((a, b) => a + b, 0) === starters.length) {
            const rows = []; let idx = 0;
            for (const size of sizes) { rows.push(starters.slice(idx, idx + size)); idx += size; }
            return rows.filter(r => r.length > 0);
        }
    }
    const pos = p => (p.position || '').toLowerCase();
    const gk  = starters.filter(p => pos(p).includes('goal') || pos(p) === 'g');
    const def = starters.filter(p => pos(p).includes('defend'));
    const mid = starters.filter(p => pos(p).includes('mid'));
    const fwd = starters.filter(p => pos(p).includes('attack') || pos(p).includes('forward') || pos(p).includes('striker'));
    if (gk.length && (def.length || mid.length || fwd.length)) {
        if (formationStr) {
            const parts = formationStr.split('-').map(Number).filter(n => n > 0);
            if (parts.length >= 4) {
                const attacking = [...mid, ...fwd];
                const midRows = []; let idx = 0;
                for (let i = 1; i < parts.length - 1; i++) {
                    midRows.push(attacking.slice(idx, idx + parts[i])); idx += parts[i];
                }
                return [gk, def, ...midRows.filter(r => r.length), attacking.slice(idx)].filter(r => r.length > 0);
            }
        }
        return [gk, def, mid, fwd].filter(r => r.length > 0);
    }
    return [starters];
}

function MiniPitchPlayer({ player }) {
    const [err, setErr] = useState(false);
    const useWcImages = useContext(WcImagesCtx);
    const name     = player.name || player.player || '';
    const shirt    = player.shirt || player.shirt_number || '?';
    const lastName = name ? name.split(' ').pop() : shirt;
    const hasPhoto = !err && player.player_id && player.player_id !== '0';
    return (
        <div className="flex flex-col items-center gap-0.5 w-full px-0.5">
            {/* Circle — smaller on mobile, larger on desktop */}
            <div className="relative flex-shrink-0">
                <div
                    className="w-12 h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden border-2 border-white/50 bg-slate-700 flex items-center justify-center shadow-md"
                    style={hasPhoto && useWcImages ? {
                        backgroundImage: `url(${wcPlayerUrl(player.player_id)})`,
                        backgroundSize: 'auto 230%',
                        backgroundPosition: '50% 5%',
                        backgroundRepeat: 'no-repeat',
                    } : undefined}
                >
                    {hasPhoto && useWcImages ? (
                        <img src={wcPlayerUrl(player.player_id)} alt="" className="sr-only" onError={() => setErr(true)} />
                    ) : hasPhoto ? (
                        <img
                            src={playerUrl(player.player_id)}
                            alt={name}
                            className="w-full h-full object-cover"
                            onError={() => setErr(true)}
                        />
                    ) : (
                        <span className="text-[10px] lg:text-sm font-bold text-white leading-none">{shirt}</span>
                    )}
                </div>
                {/* Sub-on badge */}
                {player.subbed_on && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 lg:w-4 lg:h-4
                        rounded-full bg-green-500 border border-black/40 flex items-center justify-center shadow">
                        <span className="text-[7px] lg:text-[8px] font-black text-white leading-none">↑</span>
                    </span>
                )}
            </div>
            <span className="text-[10px] lg:text-[11px] text-white/90 font-semibold text-center leading-tight w-full truncate drop-shadow">
                {lastName}
            </span>
        </div>
    );
}

// Landscape: each formation row becomes a vertical column of players
function MiniPitchColumn({ players }) {
    return (
        <div className="flex-1 flex flex-col items-center justify-around h-full py-2">
            {(players || []).map((p, i) => (
                <MiniPitchPlayer key={p.player_id || i} player={p}/>
            ))}
        </div>
    );
}

// Portrait: each formation row is a horizontal band of players
function MiniPitchRow({ players }) {
    return (
        <div className="flex-1 flex flex-row items-center justify-around w-full px-2">
            {(players || []).map((p, i) => (
                <MiniPitchPlayer key={p.player_id || i} player={p}/>
            ))}
        </div>
    );
}

/**
 * Apply substitutions to both teams' player lists.
 * Mirrors computePitchAtMinute in MatchDetailPage exactly:
 *   PA API — player_id (player1) = coming ON, player2_id (player2) = going OFF
 * Critically, the incoming player inherits the outgoing player's
 * formation_place so they appear in the correct pitch row.
 */
function applyAllSubstitutions(lineupsData, subs) {
    if (!lineupsData || !subs?.length) return lineupsData;

    // Work on copies; keep originals for reverse-lookups
    const origHome = lineupsData?.home?.players || [];
    const origAway = lineupsData?.away?.players || [];
    const home = origHome.map(p => ({ ...p }));
    const away = origAway.map(p => ({ ...p }));

    for (const sub of subs) {
        if (sub.type !== 'substitution') continue;
        // Try home, then away — whichever has the going-off player as a starter
        for (const [players, origPlayers] of [[home, origHome], [away, origAway]]) {
            const offIdx = players.findIndex(
                p => String(p.player_id) === String(sub.player2_id) && p.status === 'Start'
            );
            if (offIdx === -1) continue;
            const goingOff = players[offIdx];
            // Find coming-on player in original roster (may be listed as sub there)
            const comingOn = origPlayers.find(p => String(p.player_id) === String(sub.player_id));
            players[offIdx] = comingOn
                ? { ...comingOn, status: 'Start', position: goingOff.position, formation_place: goingOff.formation_place, subbed_on: true }
                : { ...goingOff, player_id: sub.player_id, name: sub.player || '', player: sub.player || '', subbed_on: true };
            break; // applied — don't check the other team
        }
    }

    return {
        ...lineupsData,
        home: { ...lineupsData.home, players: home },
        away: { ...lineupsData.away, players: away },
    };
}

function LineupsIdleScreen({ lineups, allEvents, homeTeamId, awayTeamId, homeTeamName, awayTeamName }) {
    // Hooks must be at the top — before any early returns
    const [activePanel, setActivePanel] = useState(0);
    const swipeRef = useRef(null);
    const onSwipeScroll = () => {
        if (!swipeRef.current) return;
        const { scrollLeft, offsetWidth } = swipeRef.current;
        setActivePanel(scrollLeft > offsetWidth / 2 ? 1 : 0);
    };

    if (!lineups) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 h-full">
                <Loader2 className="h-5 w-5 animate-spin text-slate-600"/>
                <p className="text-xs text-slate-600">Loading line-ups…</p>
            </div>
        );
    }

    const subs = allEvents?.filter(e => e.type === 'substitution') || [];
    const updatedLineups = applyAllSubstitutions(lineups, subs);

    const home = updatedLineups.home;
    const away = updatedLineups.away;
    const homeRows = groupByFormationMV(home?.formation, home?.players);
    const awayRows = groupByFormationMV(away?.formation, away?.players);

    // Shared grass + markings for the pitch background
    const PitchBgLandscape = () => (
        <>
            <div className="absolute inset-0 flex">
                {[...Array(14)].map((_, i) => (
                    <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-emerald-700' : 'bg-emerald-600'}`}/>
                ))}
            </div>
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-2 border border-white/20 rounded-sm"/>
                <div className="absolute top-2 bottom-2 left-1/2 w-px bg-white/25"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-white/20"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white/35"/>
                <div className="absolute left-2 top-1/2 -translate-y-1/2 border border-white/18" style={{ height: '55%', width: '11%', borderLeftWidth: 0 }}/>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 border border-white/18" style={{ height: '55%', width: '11%', borderRightWidth: 0 }}/>
                <div className="absolute left-2 top-1/2 -translate-y-1/2 border border-white/10" style={{ height: '28%', width: '4.5%', borderLeftWidth: 0 }}/>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 border border-white/10" style={{ height: '28%', width: '4.5%', borderRightWidth: 0 }}/>
            </div>
        </>
    );

    const PitchBgPortrait = () => (
        <>
            <div className="absolute inset-0 flex flex-col">
                {[...Array(14)].map((_, i) => (
                    <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-emerald-700' : 'bg-emerald-600'}`}/>
                ))}
            </div>
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-2 border border-white/20 rounded-sm"/>
                {/* Centre line — horizontal */}
                <div className="absolute left-2 right-2 top-1/2 h-px bg-white/25"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-white/20"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white/35"/>
                {/* Top penalty area (home) */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 border border-white/18" style={{ width: '55%', height: '12%', borderTopWidth: 0 }}/>
                {/* Bottom penalty area (away) */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 border border-white/18" style={{ width: '55%', height: '12%', borderBottomWidth: 0 }}/>
                {/* 6-yard boxes */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 border border-white/10" style={{ width: '28%', height: '5%', borderTopWidth: 0 }}/>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 border border-white/10" style={{ width: '28%', height: '5%', borderBottomWidth: 0 }}/>
            </div>
        </>
    );

    // Shared team name header strip
    const teamStrip = (
        <div className="flex items-center justify-between px-3 py-1.5 flex-shrink-0 border-b border-white/6 bg-black/20">
            <div className="flex items-center gap-1.5 min-w-0">
                <TeamBadgeSm teamId={homeTeamId} teamName={homeTeamName}/>
                <span className="text-emerald-300 font-bold text-xs truncate max-w-[80px]">{homeTeamName}</span>
                {home?.formation && <span className="text-[10px] text-slate-500 flex-shrink-0">{home.formation}</span>}
            </div>
            <span className="text-[9px] text-slate-600 font-bold uppercase tracking-widest flex-shrink-0 px-2">Line-ups</span>
            <div className="flex items-center gap-1.5 min-w-0 flex-row-reverse">
                <TeamBadgeSm teamId={awayTeamId} teamName={awayTeamName}/>
                <span className="text-blue-300 font-bold text-xs truncate max-w-[80px]">{awayTeamName}</span>
                {away?.formation && <span className="text-[10px] text-slate-500 flex-shrink-0">{away.formation}</span>}
            </div>
        </div>
    );

    // Single-team portrait pitch used in the swipe panels
    const SingleTeamPitch = ({ rows, teamId, teamName, formation, colour }) => (
        <div className="flex-none w-full h-full snap-center relative overflow-hidden">
            {/* Horizontal grass stripes */}
            <div className="absolute inset-0 flex flex-col">
                {[...Array(12)].map((_, i) => (
                    <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-emerald-700' : 'bg-emerald-600'}`}/>
                ))}
            </div>
            {/* Pitch markings — goal at top, halfway line at bottom */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-x-2 top-2 bottom-2 border border-white/15 rounded-sm"/>
                {/* Goal area */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 border border-white/20"
                    style={{ width: '28%', height: '5%', borderTopWidth: 0 }}/>
                {/* Penalty area */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 border border-white/15"
                    style={{ width: '58%', height: '13%', borderTopWidth: 0 }}/>
                {/* Halfway line */}
                <div className="absolute bottom-2 left-2 right-2 h-px bg-white/25"/>
            </div>
            {/* Team name + formation overlaid at top */}
            <div className="absolute top-2 left-3 right-3 flex items-center gap-1.5 z-10 pointer-events-none">
                <TeamBadgeSm teamId={teamId} teamName={teamName}/>
                <span className="font-bold text-xs drop-shadow" style={{ color: colour }}>{teamName}</span>
                {formation && <span className="text-[10px] text-white/40">{formation}</span>}
            </div>
            {/* Players — GK row at top, striker row at bottom */}
            <div className="absolute inset-0 flex flex-col pt-8 pb-1">
                {rows.map((row, i) => <MiniPitchRow key={i} players={row}/>)}
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full w-full overflow-hidden">
            {teamStrip}

            {/* ── Mobile: swipe left/right between teams ── */}
            <div className="flex-1 overflow-hidden lg:hidden relative">
                <div
                    ref={swipeRef}
                    onScroll={onSwipeScroll}
                    className="flex h-full overflow-x-auto snap-x snap-mandatory"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    <SingleTeamPitch
                        rows={homeRows} teamId={homeTeamId} teamName={homeTeamName}
                        formation={home?.formation} colour="#10b981"
                    />
                    <SingleTeamPitch
                        rows={awayRows} teamId={awayTeamId} teamName={awayTeamName}
                        formation={away?.formation} colour="#60a5fa"
                    />
                </div>
                {/* Swipe indicator dots */}
                <div className="absolute bottom-2 inset-x-0 flex justify-center gap-2 pointer-events-none">
                    <div className={`h-1 w-6 rounded-full transition-all duration-300 ${activePanel === 0 ? 'bg-emerald-400' : 'bg-white/20'}`}/>
                    <div className={`h-1 w-6 rounded-full transition-all duration-300 ${activePanel === 1 ? 'bg-blue-400' : 'bg-white/20'}`}/>
                </div>
            </div>

            {/* ── Desktop: landscape pitch side by side ── */}
            <div className="flex-1 relative overflow-hidden hidden lg:block">
                <PitchBgLandscape/>
                <div className="absolute inset-0 flex">
                    <div className="flex-1 flex flex-row">
                        {homeRows.map((col, i) => <MiniPitchColumn key={i} players={col}/>)}
                    </div>
                    <div className="flex-1 flex flex-row-reverse">
                        {awayRows.map((col, i) => <MiniPitchColumn key={i} players={col}/>)}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Dispatcher ─────────────────────────────────────────────────────────────────

function EventScene({ event, eventIdx, allEvents, homeTeamId, awayTeamId, homeKeeper, awayKeeper, statsShot }) {
    if (!event) return null;
    if (isShot(event.type))
        return <ShotScene event={event} homeTeamId={homeTeamId} awayTeamId={awayTeamId}
                    homeKeeper={homeKeeper} awayKeeper={awayKeeper}
                    allEvents={allEvents} eventIdx={eventIdx}
                    statsShot={statsShot}/>;
    if (isSaveEvt(event.type))
        return <SaveScene event={event}/>;
    if (isCard(event.type))
        return <CardScene event={event} homeTeamId={homeTeamId}/>;
    if (isFoul(event.type))
        return <FoulScene event={event} homeTeamId={homeTeamId}/>;
    if (isSub(event.type))
        return <SubScene  event={event} homeTeamId={homeTeamId}/>;
    if (isSetPiece(event.type))
        return <SetPieceScene event={event} homeTeamId={homeTeamId}/>;
    if (isSimpleHero(event.type))
        return <SimpleHeroScene event={event} homeTeamId={homeTeamId}/>;
    return <GenericScene event={event} homeTeamId={homeTeamId}/>;
}

// ── xG Race Chart ─────────────────────────────────────────────────────────────

function XgRaceChart({ statsShots, homeStatsTeamId, awayStatsTeamId, currentMinute }) {
    const { homePoints, awayPoints, maxXg, maxMin } = useMemo(() => {
        const all = (statsShots || []).filter(s => s.expected_goals != null && s.minute != null);
        if (!all.length) return { homePoints: [], awayPoints: [], maxXg: 1, maxMin: 90 };

        const sorted = [...all].sort((a, b) => a.minute - b.minute);

        // Visible shots: up to currentMinute (or all if none selected)
        const visible = currentMinute != null
            ? sorted.filter(s => (s.minute || 0) <= currentMinute)
            : sorted;

        let hXg = 0, aXg = 0;
        const hp = [[0, 0]], ap = [[0, 0]];
        for (const s of visible) {
            const xg = s.expected_goals || 0;
            const min = s.minute || 0;
            if (s.team_id === homeStatsTeamId) { hXg += xg; hp.push([min, hXg]); }
            else                               { aXg += xg; ap.push([min, aXg]); }
        }

        // Scale uses full match so axis doesn't jump
        const totalH = sorted.filter(s => s.team_id === homeStatsTeamId)
            .reduce((t, s) => t + (s.expected_goals || 0), 0);
        const totalA = sorted.filter(s => s.team_id !== homeStatsTeamId)
            .reduce((t, s) => t + (s.expected_goals || 0), 0);

        return {
            homePoints: hp, awayPoints: ap,
            maxXg: Math.max(totalH, totalA, 0.5),
            maxMin: Math.max(90, ...sorted.map(s => s.minute || 0)),
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statsShots, homeStatsTeamId, currentMinute]);

    if (!(statsShots || []).length) return null;

    const W = 400, H = 60;
    const PL = 26, PR = 6, PT = 6, PB = 16;
    const cW = W - PL - PR, cH = H - PT - PB;

    const toX = min => PL + (Math.min(min, maxMin) / maxMin) * cW;
    const toY = xg  => PT + cH - (Math.min(xg, maxXg) / maxXg) * cH;

    const stepPath = (pts) => {
        if (pts.length < 2) return '';
        let d = `M ${toX(pts[0][0])} ${toY(pts[0][1])}`;
        for (let i = 1; i < pts.length; i++)
            d += ` H ${toX(pts[i][0])} V ${toY(pts[i][1])}`;
        return d;
    };

    const homePath = stepPath(homePoints);
    const awayPath = stepPath(awayPoints);
    const markerX  = currentMinute != null ? toX(Math.min(currentMinute, maxMin)) : null;
    const hFinal   = homePoints[homePoints.length - 1]?.[1] ?? 0;
    const aFinal   = awayPoints[awayPoints.length - 1]?.[1] ?? 0;

    return (
        <div className="flex-shrink-0 px-3 pt-1.5 pb-0 border-t border-white/6 bg-black/25">
            <div className="flex items-center justify-between mb-0.5">
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">xG Race</span>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-emerald-400 tabular-nums">{hFinal.toFixed(2)}</span>
                    <span className="text-[9px] text-slate-600">–</span>
                    <span className="text-[10px] font-bold text-blue-400 tabular-nums">{aFinal.toFixed(2)}</span>
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 50 }}>
                {/* Background */}
                <rect x={PL} y={PT} width={cW} height={cH} fill="rgba(255,255,255,0.02)" rx="2"/>
                {/* Grid: halftime + 90 */}
                {[45, 90].map(m => (
                    <line key={m} x1={toX(m)} y1={PT} x2={toX(m)} y2={PT + cH}
                        stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="3 2"/>
                ))}
                {/* Axis */}
                <line x1={PL} y1={PT + cH} x2={PL + cW} y2={PT + cH}
                    stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                {/* xG lines */}
                {homePath && <path d={homePath} fill="none" stroke="#10b981" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>}
                {awayPath && <path d={awayPath} fill="none" stroke="#3b82f6" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"/>}
                {/* Current-minute marker */}
                {markerX != null && (
                    <line x1={markerX} y1={PT} x2={markerX} y2={PT + cH}
                        stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeDasharray="3 2"/>
                )}
                {/* Y-axis labels */}
                <text x={PL - 3} y={PT + 5} textAnchor="end" fontSize="7"
                    fill="rgba(255,255,255,0.2)">{maxXg.toFixed(1)}</text>
                <text x={PL - 3} y={PT + cH + 1} textAnchor="end" fontSize="7"
                    fill="rgba(255,255,255,0.2)">0</text>
                {/* X-axis labels */}
                {[45, 90].map(m => (
                    <text key={m} x={toX(m)} y={PT + cH + 11} textAnchor="middle" fontSize="7"
                        fill="rgba(255,255,255,0.18)">{m}</text>
                ))}
            </svg>
        </div>
    );
}

// ── Stats bar ──────────────────────────────────────────────────────────────────

function StatsBar({ events, homeTeamId, homeTeamName, awayTeamName }) {
    const awayId = events.find(e => e.team_id && e.team_id !== homeTeamId)?.team_id;
    const cnt  = (type, id) => events.filter(e => e.team_id === id && e.type === type).length;
    const shotT = ['goal','own goal','penalty goal','shot on target',
                   'shot off target','blocked shot','penalty miss'];
    const hS  = events.filter(e => e.team_id === homeTeamId && shotT.includes(e.type)).length;
    const aS  = events.filter(e => e.team_id === awayId     && shotT.includes(e.type)).length;
    const hOT = events.filter(e => e.team_id === homeTeamId &&
        ['goal','own goal','penalty goal','shot on target'].includes(e.type)).length;
    const aOT = events.filter(e => e.team_id === awayId &&
        ['goal','own goal','penalty goal','shot on target'].includes(e.type)).length;

    const stat = (label, h, a) => (
        <div key={label} className="flex items-center gap-1.5 px-2">
            <span className="text-emerald-400 text-xs font-bold tabular-nums w-4 text-right">{h}</span>
            <span className="text-slate-500 text-[10px] font-medium whitespace-nowrap">{label}</span>
            <span className="text-blue-400 text-xs font-bold tabular-nums w-4 text-left">{a}</span>
        </div>
    );

    return (
        <div className="flex-shrink-0 flex items-center justify-center flex-wrap gap-0.5
            py-2 px-2 border-t border-white/10 bg-black/30">
            <span className="text-[11px] text-emerald-400 font-bold mr-1 truncate max-w-[80px]">
                {homeTeamName}
            </span>
            {stat('Shots', hS, aS)}
            {stat('On target', hOT, aOT)}
            {stat('Yellow', cnt('yellow card', homeTeamId), cnt('yellow card', awayId))}
            {stat('Red', cnt('red card', homeTeamId), cnt('red card', awayId))}
            {stat('Fouls', cnt('foul', homeTeamId), cnt('foul', awayId))}
            {stat('Corners', cnt('corner', homeTeamId), cnt('corner', awayId))}
            <span className="text-[11px] text-blue-400 font-bold ml-1 truncate max-w-[80px]">
                {awayTeamName}
            </span>
        </div>
    );
}

// ── Timeline item ──────────────────────────────────────────────────────────────

function TimelineItem({ event, isActive, homeTeamId, onClick }) {
    const isHome = event.team_id === homeTeamId;
    const goal   = isGoal(event.type);
    const dotCol = EVENT_DOT[event.type] || '#64748b';
    const teamCol = isHome ? '#10b981' : '#3b82f6';

    return (
        <button
            onClick={onClick}
            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left
                transition-all rounded-lg border
                ${isActive
                    ? 'bg-white/10 border-white/20 shadow-sm'
                    : 'hover:bg-white/5 border-transparent'}
                ${goal && !isActive ? 'border-emerald-500/25 bg-emerald-500/5' : ''}`}
        >
            {/* Team colour left bar */}
            <div className="w-0.5 self-stretch rounded-full flex-shrink-0"
                style={{ background: teamCol, minHeight: 28 }}/>

            {/* Event type dot */}
            <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                style={{ background: `${dotCol}22`, border: `1px solid ${dotCol}55` }}>
                <span className="text-[8px] font-black" style={{ color: dotCol }}>
                    {EVENT_SHORT[event.type] || '?'}
                </span>
            </div>

            {/* Player photo */}
            <PlayerPhoto
                playerId={event.player_id}
                playerName={event.player}
                size="sm"
                ringColor={teamCol}
                className="flex-shrink-0"
            />

            {/* Text */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                    <span className={`text-[10px] font-mono flex-shrink-0 ${isHome ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {event.display_time}
                    </span>
                </div>
                <p className={`text-[11px] font-medium truncate leading-tight
                    ${goal ? 'text-emerald-300' : 'text-slate-200'}`}>
                    {event.player || event.type}
                </p>
                {event.player2 && (
                    <p className="text-[9px] text-slate-500 truncate">{event.player2}</p>
                )}
            </div>
            {isActive && <ChevronRight className="h-3 w-3 text-slate-500 flex-shrink-0"/>}
        </button>
    );
}

// ── Main modal ────────────────────────────────────────────────────────────────

const REPLAY_SPEEDS = [1, 2, 4];

// Key events shown in default timeline mode
const KEY_EVENT_TYPES = new Set([
    'goal','own goal','penalty goal','penalty miss',
    'shot on target','shot off target','blocked shot',
    'yellow card','red card','yellow/red card',
    'substitution','foul','corner','offside',
    'free kick','penalty','save',
]);

export default function MatchViewModal({ matchId, matchMeta, liveStatus, onClose }) {
    const [data,          setData]          = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [lineups,       setLineups]       = useState(null);
    const [activeIdx,     setActiveIdx]     = useState(null);
    const [replayIdx,     setReplayIdx]     = useState(-1);
    const [isPlaying,     setIsPlaying]     = useState(false);
    const [speedIdx,      setSpeedIdx]      = useState(0);
    const [isLive,        setIsLive]        = useState(false);
    const [showAllEvents, setShowAllEvents] = useState(false);
    const [newEventCount, setNewEventCount] = useState(0);
    const [flashEvent,    setFlashEvent]    = useState(null); // auto-flashed live event
    const [shotmap,       setShotmap]       = useState(null); // theStatsAPI enrichment (optional)
    const replayTimer      = useRef(null);
    const liveTimer        = useRef(null);
    const flashTimerRef    = useRef(null);
    const prevEventCount   = useRef(0);
    const timelineRef      = useRef(null);
    const speed = REPLAY_SPEEDS[speedIdx];

    // Fetch lineups once — used for goalkeeper lookup
    useEffect(() => {
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/lineups`,
            { headers: { Authorization: `Bearer ${getToken()}` } })
            .then(r => setLineups(r.data))
            .catch(() => {});
    }, [matchId]);

    // Fetch shotmap for xG enrichment — silently ignored if not available
    useEffect(() => {
        axios.get(`${API_URL}/api/football/match/${matchId}/shotmap`,
            { headers: { Authorization: `Bearer ${getToken()}` } })
            .then(r => setShotmap(r.data))
            .catch(() => {});
    }, [matchId]);

    // Extract goalkeepers from lineup data
    // Lineups API uses p.shirt (not p.shirt_number) and p.name (not p.player)
    const homeKeeper = useMemo(() =>
        lineups?.home?.players?.find(p =>
            (p.position || '').toLowerCase().includes('goal') ||
            parseInt(p.shirt || p.shirt_number) === 1
        ), [lineups]);
    const awayKeeper = useMemo(() =>
        lineups?.away?.players?.find(p =>
            (p.position || '').toLowerCase().includes('goal') ||
            parseInt(p.shirt || p.shirt_number) === 1
        ), [lineups]);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/football/hub/match/${matchId}/matchview`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setData(prev => {
                if (prev && silent) {
                    const added = (res.data.events || []).length - (prev.events?.length || 0);
                    if (added > 0) setNewEventCount(c => c + added);
                }
                return res.data;
            });
        } catch (e) {
            console.error('MatchView fetch error', e);
        } finally {
            setLoading(false);
        }
    }, [matchId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-enable live polling + ALL-events mode when match is in progress
    useEffect(() => {
        if (data?.live_minute && !data?.is_result) {
            setIsLive(true);
            setShowAllEvents(true);
        } else if (data?.is_result) {
            setShowAllEvents(false); // finished → KEY events only
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.live_minute, data?.is_result]);

    // Auto-flash new events that arrive during live polling, then return to lineup.
    // Use data?.events directly (not allRawEvents) — allRawEvents is declared later
    // in the component body and its .length in the dep array would be a TDZ error.
    useEffect(() => {
        if (!isLive) return;
        const evts = data?.events || [];
        const currentCount = evts.length;
        if (currentCount > prevEventCount.current && prevEventCount.current > 0) {
            const newest = [...evts]
                .slice(prevEventCount.current)
                .reverse()
                .find(e => e.type !== 'block');
            if (newest) {
                setActiveIdx(null);
                setIsPlaying(false);
                setFlashEvent(newest);
                clearTimeout(flashTimerRef.current);
                flashTimerRef.current = setTimeout(() => setFlashEvent(null), 6000);
            }
        }
        prevEventCount.current = currentCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.events?.length, isLive]);

    useEffect(() => {
        if (!isLive) { clearInterval(liveTimer.current); return; }
        liveTimer.current = setInterval(() => fetchData(true), 15000);
        return () => clearInterval(liveTimer.current);
    }, [isLive, fetchData]);

    useEffect(() => {
        clearInterval(replayTimer.current);
        if (!isPlaying || !data) return;
        const evts = data.events || [];
        replayTimer.current = setInterval(() => {
            setReplayIdx(i => {
                const next = i + 1;
                if (next >= evts.length) { setIsPlaying(false); return evts.length - 1; }
                setActiveIdx(next);
                return next;
            });
        }, 1200 / speed);
        return () => clearInterval(replayTimer.current);
    }, [isPlaying, speed, data]);

    useEffect(() => {
        if (activeIdx === null || !timelineRef.current) return;
        const el = timelineRef.current.querySelector(`[data-idx="${activeIdx}"]`);
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeIdx]);

    // Full event list used for blocker/whereTo lookups inside ShotScene
    const allRawEvents  = data?.events || [];
    // Timeline: ALL mode shows everything except block internals; KEY mode filters to highlight events
    const events = showAllEvents
        ? allRawEvents.filter(e => e.type !== 'block')
        : allRawEvents.filter(e => KEY_EVENT_TYPES.has(e.type));
    const activeEvent   = activeIdx !== null ? events[activeIdx] : null;
    // data.is_result (PA match/events isResult:yes) is the primary finished signal.
    // liveStatus explicit phases are a secondary signal.
    const _MV_FINISHED_PHASES = ['full_time', 'after_et', 'abandoned', 'postponed'];
    const isFinished = data?.is_result === true
        || ((liveStatus && liveStatus.phase !== 'unknown') && _MV_FINISHED_PHASES.includes(liveStatus.phase));

    // What to show in the visualisation panel:
    //   flashEvent  → auto-flashed live event (returns to lineup after 6s)
    //   activeEvent → manually selected from timeline
    //   null        → lineup idle screen
    const displayEvent = flashEvent ?? activeEvent;
    const displayIdx   = flashEvent
        ? allRawEvents.findIndex(e => e === flashEvent)
        : activeIdx;

    const handlePlayPause = () => {
        if (replayIdx < 0) { setReplayIdx(0); setActiveIdx(0); setIsPlaying(true); return; }
        setIsPlaying(p => !p);
    };
    const step = (dir) => {
        setIsPlaying(false);
        setActiveIdx(i => {
            const next = Math.max(0, Math.min(events.length - 1, (i ?? 0) + dir));
            setReplayIdx(next);
            return next;
        });
    };

    // liveStatus (match/info, 15 s poll) is the most current source for live scores
    const homeScore    = liveStatus?.home_score ?? data?.home_score ?? matchMeta?.home_score;
    const awayScore    = liveStatus?.away_score ?? data?.away_score ?? matchMeta?.away_score;
    const homeTeamName = data?.home_team_name || matchMeta?.home_team_name || 'Home';
    const awayTeamName = data?.away_team_name || matchMeta?.away_team_name || 'Away';
    const homeTeamId   = data?.home_team_id   || matchMeta?.home_team_id   || '';
    const awayTeamId   = data?.away_team_id   || matchMeta?.away_team_id   || '';
    const homeTeamBadgeId = matchMeta?.home_team_id || homeTeamId;
    const awayTeamBadgeId = matchMeta?.away_team_id || awayTeamId;

    // ── theStatsAPI shot enrichment ──────────────────────────────────────────
    const statsShots = useMemo(() => shotmap?.shots || [], [shotmap]);
    // Identify which statsapi team_id belongs to home vs away by name matching
    const homeStatsTeamId = useMemo(() => {
        const ids = [...new Set(statsShots.map(s => s.team_id))];
        const hFirst = (homeTeamName || '').split(' ')[0].toLowerCase();
        return ids.find(id => {
            const sName = (statsShots.find(s => s.team_id === id)?.team_name || '').toLowerCase();
            return hFirst && sName.includes(hFirst);
        }) ?? ids[0] ?? null;
    }, [statsShots, homeTeamName]);
    const awayStatsTeamId = useMemo(() =>
        [...new Set(statsShots.map(s => s.team_id))].find(id => id !== homeStatsTeamId) ?? null,
    [statsShots, homeStatsTeamId]);

    // Find the best-matching statsapi shot for a PA event (by minute ±1 + team side)
    const findStatsShot = useCallback((event) => {
        if (!statsShots.length) return null;
        const isHomeEvt = event.team_id === homeTeamId;
        const statsTeamId = isHomeEvt ? homeStatsTeamId : awayStatsTeamId;
        if (!statsTeamId) return null;
        const min = parseInt(event.minute) || 0;
        return statsShots
            .filter(s => s.team_id === statsTeamId && Math.abs((s.minute || 0) - min) <= 1)
            .sort((a, b) => Math.abs((a.minute || 0) - min) - Math.abs((b.minute || 0) - min))[0]
            ?? null;
    }, [statsShots, homeTeamId, homeStatsTeamId, awayStatsTeamId]);

    // If either team is a WC team, all player photos in this modal use WCPLAYERS
    const isWcMatch = isWcTeam(homeTeamId) || isWcTeam(awayTeamId);

    return (
      <WcImagesCtx.Provider value={isWcMatch}>
        <div className="fixed inset-0 z-50 flex flex-col bg-[#07101c] text-white overflow-hidden">

            {/* ── Header ── */}
            <div className="flex-shrink-0 relative flex items-center px-3 py-2.5
                bg-gradient-to-r from-[#0d1526] via-[#0a1830] to-[#0d1526]
                border-b border-white/8 shadow-2xl" style={{ minHeight: 56 }}>

                {/* Score block — absolutely centred; no team names, just badge · score · badge */}
                <div className="absolute inset-x-0 flex flex-col items-center pointer-events-none">
                    <div className="flex items-center gap-4">
                        <TeamBadgeSm teamId={homeTeamBadgeId} teamName={homeTeamName}/>

                        {homeScore !== null && homeScore !== undefined ? (
                            <span className="font-black text-2xl tabular-nums tracking-wider
                                px-5 py-1 rounded-xl bg-white/10 border border-white/15 flex-shrink-0">
                                {homeScore} – {awayScore}
                            </span>
                        ) : (
                            <span className="font-black text-xl text-slate-500 px-3 flex-shrink-0">vs</span>
                        )}

                        <TeamBadgeSm teamId={awayTeamBadgeId} teamName={awayTeamName}/>
                    </div>

                    {/* Phase label */}
                    {liveStatus?.display ? (
                        <span className={`text-[9px] font-bold mt-0.5 ${
                            liveStatus.phase === 'half_time'    ? 'text-amber-300' :
                            liveStatus.phase === 'et_half_time' ? 'text-amber-300' :
                            liveStatus.phase === 'full_time'    ? 'text-slate-400' :
                            liveStatus.phase === 'after_et'     ? 'text-slate-400' :
                            liveStatus.phase === 'penalties'    ? 'text-purple-300' :
                            'text-green-400'
                        }`}>{liveStatus.display}</span>
                    ) : isFinished ? (
                        <span className="text-[9px] text-slate-500 font-semibold uppercase tracking-widest mt-0.5">Full Time</span>
                    ) : data?.live_minute ? (
                        <span className="text-[9px] text-green-400 font-bold mt-0.5">
                            {matchMeta?.phase_label ? `${matchMeta.phase_label} · ` : ''}{data.live_minute}' LIVE
                        </span>
                    ) : matchMeta?.phase_label ? (
                        <span className="text-[9px] text-green-400 font-bold mt-0.5">{matchMeta.phase_label} · LIVE</span>
                    ) : null}
                </div>

                {/* LIVE button — left side so it doesn't push the score */}
                <div className="flex items-center gap-1 z-10">
                    {!isFinished && liveStatus?.phase !== 'full_time' && (
                        <button
                            onClick={() => { setIsLive(l => !l); setNewEventCount(0); }}
                            className={`flex items-center gap-1.5 text-[10px] px-2 py-1
                                rounded-full border font-semibold transition-all
                                ${isLive
                                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                                    : 'bg-white/5 border-white/15 text-slate-500 hover:border-white/30'}`}
                        >
                            {isLive ? (
                                <>
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inset-0 rounded-full bg-red-400 opacity-75"/>
                                        <span className="relative rounded-full h-2 w-2 bg-red-500"/>
                                    </span>
                                    LIVE
                                    {newEventCount > 0 && (
                                        <span className="bg-red-500 text-white text-[9px] rounded-full px-1 font-bold">
                                            +{newEventCount}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <><Radio className="h-3 w-3"/> Live</>
                            )}
                        </button>
                    )}
                </div>

                {/* Close button — right side */}
                <button onClick={onClose}
                    className="ml-auto flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors z-10">
                    <X className="h-5 w-5"/>
                </button>
            </div>

            {/* ── Replay controls ── */}
            <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5
                bg-black/40 border-b border-white/6">
                <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => step(-1)} disabled={!events.length}>
                    <SkipBack className="h-3.5 w-3.5"/>
                </Button>
                <Button size="icon" variant="ghost"
                    className={`h-7 w-7 ${isPlaying ? 'text-emerald-400' : ''}`}
                    onClick={handlePlayPause} disabled={!events.length || loading}>
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> :
                     isPlaying ? <Pause className="h-3.5 w-3.5"/> : <Play className="h-3.5 w-3.5"/>}
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => step(1)} disabled={!events.length}>
                    <SkipForward className="h-3.5 w-3.5"/>
                </Button>
                <button
                    onClick={() => setSpeedIdx(i => (i + 1) % REPLAY_SPEEDS.length)}
                    className="text-[11px] px-2 py-0.5 rounded border border-white/15 text-slate-500
                        hover:border-white/30 hover:text-white transition-colors font-mono">
                    {speed}×
                </button>
                {/* KEY / ALL events toggle */}
                <button
                    onClick={() => { setShowAllEvents(v => !v); setActiveIdx(null); setReplayIdx(-1); setIsPlaying(false); }}
                    className={`text-[11px] px-2 py-0.5 rounded border font-bold transition-colors
                        ${showAllEvents
                            ? 'bg-white/10 border-white/30 text-white'
                            : 'border-white/15 text-slate-500 hover:border-white/30 hover:text-slate-200'}`}>
                    {showAllEvents ? 'ALL' : 'KEY'}
                </button>
                <div className="flex-1 mx-2 h-1 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500/80 transition-all duration-300 rounded-full"
                        style={{ width: events.length > 0
                            ? `${((activeIdx ?? -1) + 1) / events.length * 100}%` : '0%' }}/>
                </div>
                <span className="text-[10px] text-slate-600 font-mono tabular-nums flex-shrink-0">
                    {activeIdx !== null ? activeIdx + 1 : 0}/{events.length}
                </span>
            </div>

            {/* ── Main content ── */}
            {/*
                Mobile:  flex-col — visualization on top (flex-1), timeline strip below (fixed h-44)
                Desktop: flex-row — timeline sidebar left (w-40), visualization right (flex-1)
            */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">

                {/* Visualisation — top on mobile, right on desktop */}
                <div className="flex-1 flex flex-col overflow-hidden min-h-0 lg:order-2">
                    {/* Event type header */}
                    {displayEvent && (
                        <div className={`flex-shrink-0 flex items-center gap-2
                            py-2 px-3 border-b border-white/6 text-xs font-bold uppercase tracking-widest
                            ${isGoal(displayEvent.type)
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : 'bg-white/3 text-slate-500'}`}>
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: EVENT_DOT[displayEvent.type] || '#64748b' }}/>
                            <span className="truncate">{displayEvent.type}</span>
                            <span className="text-slate-600 font-normal normal-case tracking-normal flex-shrink-0">
                                · {displayEvent.display_time}
                            </span>
                            {/* Auto-flash badge — tap to dismiss and return to lineup */}
                            {flashEvent && (
                                <button
                                    onClick={() => { clearTimeout(flashTimerRef.current); setFlashEvent(null); }}
                                    className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400 font-semibold flex-shrink-0">
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="animate-ping absolute inset-0 rounded-full bg-emerald-400 opacity-75"/>
                                        <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-500"/>
                                    </span>
                                    LIVE
                                </button>
                            )}
                        </div>
                    )}
                    <div className="flex-1 overflow-y-auto">
                        {displayEvent ? (
                            <EventScene
                                event={displayEvent}
                                eventIdx={displayIdx}
                                allEvents={allRawEvents}
                                homeTeamId={homeTeamId}
                                awayTeamId={awayTeamId}
                                homeKeeper={homeKeeper}
                                awayKeeper={awayKeeper}
                                statsShot={isShot(displayEvent.type) ? findStatsShot(displayEvent) : null}
                            />
                        ) : (
                            <LineupsIdleScreen
                                lineups={lineups}
                                allEvents={allRawEvents}
                                homeTeamId={homeTeamId}
                                awayTeamId={awayTeamId}
                                homeTeamName={homeTeamName}
                                awayTeamName={awayTeamName}
                            />
                        )}
                    </div>
                </div>

                {/* Timeline —
                    Mobile:  bottom strip, fixed height, scrollable vertically
                    Desktop: left sidebar (w-40), order-1 so it renders left of visualization
                */}
                <div ref={timelineRef}
                    className="
                        flex-shrink-0 bg-black/20
                        border-t border-white/6 lg:border-t-0 lg:border-r lg:border-white/6
                        h-44 lg:h-auto lg:w-40
                        overflow-y-auto
                        py-2 px-1.5 space-y-0.5
                        lg:order-1
                    ">
                    {loading && (
                        <div className="flex justify-center py-10">
                            <Loader2 className="h-5 w-5 animate-spin text-slate-600"/>
                        </div>
                    )}
                    {!loading && events.length === 0 && (
                        <p className="text-[11px] text-slate-700 text-center py-10 px-2 leading-relaxed">
                            No events yet
                        </p>
                    )}
                    {events.map((ev, idx) => (
                        <div key={ev.id || idx} data-idx={idx}>
                            <TimelineItem
                                event={ev}
                                isActive={activeIdx === idx}
                                homeTeamId={homeTeamId}
                                onClick={() => {
                                    setIsPlaying(false);
                                    setActiveIdx(idx);
                                    setReplayIdx(idx);
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* xG Race Chart */}
            {statsShots.length > 0 && (
                <XgRaceChart
                    statsShots={statsShots}
                    homeStatsTeamId={homeStatsTeamId}
                    awayStatsTeamId={awayStatsTeamId}
                    currentMinute={displayEvent ? (parseInt(displayEvent.minute) || null) : null}
                />
            )}

            {/* Stats bar */}
            {events.length > 0 && (
                <StatsBar
                    events={events}
                    homeTeamId={homeTeamId}
                    homeTeamName={homeTeamName}
                    awayTeamName={awayTeamName}
                />
            )}
        </div>
      </WcImagesCtx.Provider>
    );
}
